import {action, makeObservable, observable, runInAction, toJS} from 'mobx';

import {api} from '../../ApiFacade';
import {mainState} from '../../AppMainState';
import {AppRoutes, projectDashboardLink} from '../../routing/AppRoutes';
import {showCommunicationError, showDefaultError, showValidationError, showWarning} from '../../helpers/alerts';
import {DateInterval, LogDocument, LogType, MusculaSubscritpionPlan, UserListDto, UserStatus} from '../../swagger';
import {showLoaderAsync} from '../../helpers/loaders';
import {generateUUID} from '../../helpers/helpers';
import {redirectTo} from '../../routing/browserHistory';
import {FormSelectOption} from '../../components/form/FormSelect';

export class LogEditState {
    @observable
    projectName: string | null | undefined;
    @observable
    item: LogDocument = {name: '', projectId: ''};
    @observable
    errors: any = {};
    @observable
    projectUsers: Array<FormSelectOption> = [];

    constructor() {
        makeObservable(this);
    }

    @action
    setNewLogItem(projectId: string) {
        this.item = Object.assign({}, {
            name: '',
            notifyInterval: undefined,
            notifyOnNew: false,
            projectId: projectId,
            webhookNotifyEvery: false,
            webhookUrl: '',
            logType: LogType.GenericJs,
            timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            timeZoneOffset: new Date().getTimezoneOffset(),
            notificationEmail: mainState.currentUser!.email!
        });
    }

    initNew(projectId?: string) {
        if (!projectId) {
            throw new Error('Project ID not set');
        }
        this.setProjectId(projectId);
        this.setNewLogItem(projectId);
        this.loadProjectUsers();
    }

    setProjectId(projectId?: string) {
        this.item.projectId = projectId;
        this.projectName = mainState.projects?.find(
            (p) => p.projectId === projectId
        )?.name;
    }

    loadLogItem(logId: string) {
        showLoaderAsync(api().logs.apiLogsLogIdGet({
            logId: logId,
        })
            .then((res => {
                runInAction(() => {
                    if (res.content) {
                        this.item = res.content;
                        if (!this.item.notificationEmail) {
                            this.item.notificationEmail = mainState.currentUser?.email;
                        }
                        this.loadProjectUsers();
                    }
                });
            }))
            .catch(err => {
                showCommunicationError(err);
            }));
    }
    
    @action
    loadProjectUsers() {
        this.projectUsers = (mainState.projects.find(x => x.projectId === this.item.projectId)?.users?.filter(x => x.status === UserStatus.Active) ?? []).map(x => {
            return {
                key: x.name ?? '',
                label: x.name ?? '',
            };
        });
    }

    @action
    setName(v: string) {
        this.item.name = v;
    }

    @action
    setNotifyOnNew(v: boolean) {
        this.item.notifyOnNew = v;
    }

    @action
    setNotifyInterval(v: any) {
        if (!v) {
            v = null;
        }
        this.item.notifyInterval = v as DateInterval;
    }

    @action
    setWebhookUrl(v: string) {
        this.item.webhookUrl = v;
    }

    save() {
        if (!this.item.name) {
            this.errors.name = true;
            return;
        }

        if (this.item.uptimeCheck?.uptimeCheckUrl?.includes('localhost')) {
            this.errors.uptimeCheckUrl = true;
            return;
        }


        if (this.item.logId) {
            this.update();
        } else {
            this.create();
        }

    }

    create() {
        /*if (mainState.isDemoUser) {
            showWarning('Demo mode', 'Cannot make changes in demo mode');
            return;
        }
        */
        if (mainState.currentUser?.musculaSubscritpionPlan === MusculaSubscritpionPlan.MusculaBasic && mainState.projects.length >= 1 && mainState.projects[0].logs!.length >= 1) {
            showWarning('Limit reached', 'Please upgrade your subscription to create new projects');
            return;
        }

        api()
            .logs.apiLogsNewPost({
            logDocument: this.item,
        })
            .then((res) => {
                runInAction(() => {
                    mainState.projects
                        ?.find((x) => x.projectId === this.item.projectId)
                        ?.logs?.push({
                        logId: res.content?.id,
                        name: this.item.name
                    });
                });
                redirectTo(AppRoutes.IntegrationInstructions + '/' + (res.content?.id ?? ''));
            })
            .catch(async (err) => {
                if (err.status === 400) {
                    const errors = (await err.json()).errors;
                    if (errors?.length > 0) {
                        showWarning('Please fix these warnings', errors.join('<br/>'));
                    } else {
                        showValidationError();
                    }

                } else {
                    showDefaultError(err);
                }
            });
    }

    update() {
        if (mainState.isDemoUser) {
            showWarning('Cannot save elements in demo mode', 'Please create own account. It\'s free');
            return;
        }
        api()
            .logs.apiLogsLogIdPut({
            logDocument: this.item,
            logId: this.item.logId!,
        })
            .then(() => {
                redirectTo(projectDashboardLink(this.item.projectId!));
            })
            .catch(async (err) => {
                if (err.status === 400) {
                    const errors = (await err.json()).errors;
                    if (errors?.length > 0) {
                        showWarning('Please fix these warnings', errors.join('<br/>'));
                    } else {
                        showValidationError();
                    }

                } else {
                    showDefaultError(err);
                }
            });
    }

    @action
    setLogType(v: any) {
        this.item.logType = v;
    }

    @action
    setUptimeCheckUrl(v: string) {
        if (!this.item.uptimeCheck) {
            this.item.uptimeCheck = {};
        }

        this.item.uptimeCheck.uptimeCheckUrl = v;
    }

    @action
    setApiKey(v: string) {
        this.item.apiKey = v;
    }

    generateApiKey() {
        this.setApiKey(generateUUID());
    }

    copyApiKeyToClipBoard() {
        if (!this.item.apiKey) {
            return;
        }
        navigator.clipboard.writeText(this.item.apiKey);
    }

    @action
    setNotificationEmail(v: string) {
        this.item.notificationEmail = v;
    }
}
